import { Link } from '@tanstack/react-router'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowUpRight, ChevronDown, ChevronLeft, Ellipsis } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { copy } from '../../copy/investor-copy'
import { useBrowseStartups } from '../../hooks/use-investor-assistant'
import { ActionMenu } from '../action-menu'
import { FilterChip } from '../filter-chip'
import { formatShortDate } from '../following/dates'
import { roundLabel } from '../following/round'
import { StartupTile } from '../following/startup-tile'
import { filtersOf, type SearchChip, type Sort } from './browse-filters'
import { SearchBox } from './search-box'

export type { SearchChip }

type Row = NonNullable<
  ReturnType<typeof useBrowseStartups>['data']
>['pages'][number]['items'][number]

const statusWord = (row: Row): string | null => {
  if (row.state === 'ACTIVE' || row.state === 'CONDITION_MET')
    return copy('search.status.following')
  if (row.state === 'WAITING_CHECK' || row.state === 'WAITING_PLACE')
    return copy('search.status.waiting')
  if (row.passed) return copy('search.status.passed')
  if (row.watchdogMatch) return copy('search.status.watchdog')
  return null
}

function BrowseRow({
  row,
  selected,
  onOpen,
}: {
  row: Row
  selected: boolean
  onOpen: (startupId: string) => void
}) {
  const open = () => row.hasOpenLink && onOpen(row.id)
  const status = statusWord(row)
  const sub = [row.oneLine, row.city].filter(Boolean).join(' · ')
  return (
    <tr
      data-startup-id={row.id}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        // A followed startup stays reachable even if its profile has no open link.
        onOpen(row.id)
      }}
      className={cn(
        'cursor-pointer border-t border-border transition-colors hover:bg-white/[0.04]',
        selected && 'bg-white/5'
      )}
    >
      <td className="sf-c-name py-2.5 pl-5 pr-3 align-middle">
        <div className="flex min-w-[8rem] items-center gap-3">
          <StartupTile name={row.name} />
          <div className="min-w-0 flex-1">
            {row.hasOpenLink || status ? (
              <a
                href={`?open=${row.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  onOpen(row.id)
                }}
                className="block truncate text-sm font-semibold text-foreground hover:underline"
              >
                {row.name}
              </a>
            ) : (
              <span
                title={copy('search.private')}
                className="block truncate text-sm font-semibold text-foreground"
              >
                {row.name}
              </span>
            )}
            {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </td>
      <td className="sf-col-round whitespace-nowrap px-3 py-2.5 align-middle text-sm tabular-nums text-foreground/80">
        {row.round.amount ? roundLabel(row.round) : '—'}
      </td>
      <td className="sf-col-sector px-3 py-2.5 align-middle text-xs text-foreground/80">
        <span className="block truncate">{row.sector ?? '—'}</span>
      </td>
      <td className="sf-c-upd whitespace-nowrap px-3 py-2.5 align-middle text-xs tabular-nums text-foreground/80">
        {formatShortDate(row.joinedAt)}
      </td>
      <td className="sf-c-status px-3 py-2.5 align-middle text-xs text-muted-foreground">
        {status}
      </td>
      <td className="sf-c-act px-3 py-2.5 align-middle text-right">
        {row.hasOpenLink && (
          <ActionMenu
            label={copy('panel.aria.more', { startup: row.name })}
            actions={[
              { key: 'open', icon: ArrowUpRight, title: copy('menu.openProfile'), onSelect: open },
            ]}
            trigger={
              <button
                type="button"
                aria-label={copy('panel.aria.more', { startup: row.name })}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
              >
                <Ellipsis className="size-4" />
              </button>
            }
          />
        )}
      </td>
    </tr>
  )
}

/** Everyone on SeedForge the investor may open: a name search or chips, and the table, which loads more as it scrolls. */
export function BrowseSection({
  investorId,
  q,
  chip,
  openId,
  sort,
  onSort,
  onQuery,
  onChip,
  onClearFilters,
  onOpen,
}: {
  investorId: string
  q: string | undefined
  chip: SearchChip
  sort: Sort
  onSort: (sort: Sort) => void
  onQuery: (q: string | undefined) => void
  openId: string | null
  onChip: (chip: SearchChip) => void
  onClearFilters: () => void
  onOpen: (startupId: string) => void
}) {
  const query = useBrowseStartups(filtersOf(q, chip, sort))
  const pages = query.data?.pages ?? []
  const rows = pages.flatMap((page) => page.items)
  const total = pages[0]?.total ?? 0
  const counts = pages[0]?.counts
  // The prototype's list continues as you scroll: a line under the table asks for the next page once it is in view.
  const sentinel = useRef<HTMLDivElement>(null)
  const canLoadMore = query.hasNextPage && !query.isFetchingNextPage
  const fetchNextPage = query.fetchNextPage
  useEffect(() => {
    const node = sentinel.current
    if (!canLoadMore || !node) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) fetchNextPage()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [canLoadMore, fetchNextPage])

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/app/investor/$id/search"
          params={{ id: investorId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" />
          {copy('search.browse.back')}
        </Link>
        <ActionMenu
          label={copy('search.heading')}
          actions={[
            { key: 'joined', title: copy('search.sort'), onSelect: () => onSort('joined') },
            { key: 'name', title: copy('search.sort.name'), onSelect: () => onSort('name') },
          ]}
          trigger={
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {copy(sort === 'joined' ? 'search.sort' : 'search.sort.name')}
              <ChevronDown className="size-3.5" />
            </button>
          }
        />
      </div>
      <h2 className="mt-3 text-lg font-semibold text-foreground">
        {q ? copy('search.results', { query: q }) : copy('search.heading')}
      </h2>
      <div className="mt-3">
        <SearchBox value={q ?? ''} onChange={(value) => onQuery(value || undefined)} />
      </div>
      {!q && (
        <div className="mt-3 flex flex-wrap items-center gap-1">
          <FilterChip
            label={copy('search.chip.all')}
            count={counts?.all}
            active={chip === 'all'}
            onClick={() => onChip('all')}
          />
          <FilterChip
            label={copy('search.chip.week')}
            count={counts?.newThisWeek}
            active={chip === 'week'}
            onClick={() => onChip('week')}
          />
          <FilterChip
            label={copy('search.chip.raising')}
            count={counts?.raising}
            active={chip === 'raising'}
            onClick={() => onChip('raising')}
          />
          <FilterChip
            label={copy('search.chip.europe')}
            count={counts?.europe}
            active={chip === 'europe'}
            onClick={() => onChip('europe')}
          />
          <FilterChip
            label={copy('search.chip.focus')}
            count={counts?.fitsFocus}
            active={chip === 'focus'}
            onClick={() => onChip('focus')}
          />
        </div>
      )}

      {query.isLoading && (
        <p className="mt-4 text-sm text-muted-foreground">{copy('search.loading')}</p>
      )}
      {query.isError && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">{copy('search.error')}</p>
          <SecondaryButton size="sm" onClick={() => query.refetch()}>
            {copy('panel.retry')}
          </SecondaryButton>
        </div>
      )}
      {query.isSuccess && rows.length === 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            {q ? copy('search.empty.name', { query: q }) : copy('search.empty.filters')}
          </p>
          {(q || chip !== 'all') && (
            <SecondaryButton size="sm" onClick={onClearFilters}>
              {copy('search.clearFilters')}
            </SecondaryButton>
          )}
        </div>
      )}
      {rows.length > 0 && (
        <div
          className={cn(
            'sf-cq mt-4 overflow-hidden rounded-xl border border-border bg-card/30',
            openId !== null && 'sf-panel-open'
          )}
        >
          <table className="w-full table-fixed border-collapse text-left text-sm">
            <colgroup>
              <col />
              <col className="sf-col-round w-36" />
              <col className="sf-col-sector w-36" />
              <col className="w-28" />
              <col className="w-36" />
              <col className="w-14" />
            </colgroup>
            <thead>
              <tr>
                <th className="h-10 pl-5 pr-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {copy('search.th.startup')}
                </th>
                <th className="sf-col-round h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {copy('search.th.round')}
                </th>
                <th className="sf-col-sector h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {copy('search.th.sector')}
                </th>
                <th className="h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {copy('search.th.joined')}
                </th>
                <th className="h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <span className="sr-only">{copy('search.th.status')}</span>
                </th>
                <th className="h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <span className="sr-only">{copy('search.th.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <BrowseRow key={row.id} row={row} selected={openId === row.id} onOpen={onOpen} />
              ))}
            </tbody>
          </table>
          <div
            ref={sentinel}
            className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3"
          >
            <p className="text-xs text-muted-foreground">
              {query.hasNextPage
                ? copy('search.count.scroll', { shown: rows.length, total })
                : copy('search.count.all', { total })}
            </p>
            {/* Scrolling loads the rest; the button is the way in for a keyboard or a screen reader. */}
            {query.hasNextPage && (
              <SecondaryButton
                size="sm"
                className="sr-only focus:not-sr-only"
                disabled={query.isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {copy('search.more')}
              </SecondaryButton>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
